import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import formService, { FIELD_TYPES, FIELD_TYPE_LABELS, SYSTEM_FIELDS } from '../../services/formService'
import optionService from '../../services/optionService'
import {
  HiOutlineArrowLeft, HiOutlinePlus, HiOutlineTrash, HiOutlineChevronUp,
  HiOutlineChevronDown, HiOutlineClipboardCopy, HiOutlineExternalLink,
  HiOutlineLockClosed, HiOutlineSave, HiOutlinePhotograph,
} from 'react-icons/hi'
import toast from 'react-hot-toast'

const slugifyKey = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/đ/g, 'd').replace(/Đ/g, 'D').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '').toLowerCase() || 'field'

const CUSTOM_TYPES = FIELD_TYPES.filter((t) => t.value !== 'phone')
const publicUrl = (slug) => `${window.location.origin}/tuyen-dung/${slug}`

export default function FormBuilderEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [tab, setTab] = useState('info')
  const [form, setForm] = useState(null)
  const [info, setInfo] = useState({})
  const [fields, setFields] = useState([])
  const [savingInfo, setSavingInfo] = useState(false)
  const [savingFields, setSavingFields] = useState(false)
  const [uploadingHero, setUploadingHero] = useState(false)
  const [positions, setPositions] = useState([])   // option "position" đồng bộ với /admin/options

  useEffect(() => {
    optionService.getPublic().then((r) => {
      if (r.status === 'success') setPositions(r.data.position || [])
    }).catch(() => {})
  }, [])

  const load = async () => {
    const res = await formService.get(id)
    if (res.status === 'success') {
      setForm(res.data)
      const { title, slug, position, description, intro_content, hero_image, thank_you, is_active } = res.data
      setInfo({ title, slug, position: position || '', description: description || '', intro_content: intro_content || '', hero_image: hero_image || '', thank_you: thank_you || '', is_active })
      setFields(res.data.fields.map((f) => ({ ...f })))
    }
  }
  useEffect(() => { load() }, [id])

  const setI = (k, v) => setInfo((p) => ({ ...p, [k]: v }))

  const saveInfo = async () => {
    setSavingInfo(true)
    try {
      const res = await formService.update(id, info)
      if (res.status === 'success') { toast.success('Đã lưu thông tin'); setForm(res.data); setI('slug', res.data.slug) }
    } catch { /* interceptor */ } finally { setSavingInfo(false) }
  }

  const uploadHero = async (file) => {
    if (!file) return
    setUploadingHero(true)
    try {
      const res = await formService.upload(file, 'image')
      if (res.status === 'success') setI('hero_image', res.data.url)
    } catch { /* interceptor */ } finally { setUploadingHero(false) }
  }

  // ---- Field builder helpers ----
  const usedKeys = fields.map((f) => f.field_key)
  const addSystem = (sf) => setFields((p) => [...p, {
    field_key: sf.field_key, label: sf.label, field_type: sf.field_type,
    is_required: false, is_system: true, is_active: true, options: [],
  }])
  const addCustom = () => setFields((p) => [...p, {
    field_key: '', label: '', field_type: 'text', is_required: false, is_system: false, is_active: true, options: [],
  }])
  const setField = (i, patch) => setFields((p) => p.map((f, idx) => idx === i ? { ...f, ...patch } : f))
  const removeField = (i) => setFields((p) => p.filter((_, idx) => idx !== i))
  const move = (i, dir) => setFields((p) => {
    const j = i + dir
    if (j < 0 || j >= p.length) return p
    const a = [...p];[a[i], a[j]] = [a[j], a[i]]; return a
  })

  const saveFields = async () => {
    // chuẩn hóa: custom field thiếu key -> sinh từ label
    const payload = fields.map((f, i) => ({
      ...f,
      field_key: f.is_system ? f.field_key : (f.field_key || slugifyKey(f.label)),
      sort_order: i,
      options: Array.isArray(f.options) ? f.options : [],
    }))
    if (payload.some((f) => !f.label.trim())) { toast.error('Mỗi trường cần có nhãn hiển thị'); return }
    setSavingFields(true)
    try {
      const res = await formService.saveFields(id, payload)
      if (res.status === 'success') { toast.success('Đã lưu các trường'); setFields(res.data.fields.map((f) => ({ ...f }))) }
    } catch { /* interceptor */ } finally { setSavingFields(false) }
  }

  if (!form) return <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>

  const availableSystem = SYSTEM_FIELDS.filter((sf) => !usedKeys.includes(sf.field_key))

  const TABS = [['info', 'Thông tin & hiển thị'], ['fields', 'Trường dữ liệu'], ['preview', 'Xem trước']]

  return (
    <div>
      <button onClick={() => navigate('/admin/forms')} className="inline-flex items-center gap-1 text-sm text-secondary-500 hover:text-primary-600 mb-3">
        <HiOutlineArrowLeft className="w-4 h-4" /> Danh sách chiến dịch
      </button>
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="page-title">{form.title}</h1>
          <p className="page-subtitle">URL: <span className="text-primary-600">/tuyen-dung/{info.slug}</span> · {form.submit_count} hồ sơ</p>
        </div>
      </div>

      <div className="flex gap-2 mb-5 border-b border-secondary-100">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-primary-600 text-primary-600' : 'border-transparent text-secondary-500 hover:text-secondary-800'}`}>
            {l}
          </button>
        ))}
      </div>

      {/* TAB: THÔNG TIN */}
      {tab === 'info' && (
        <div className="card p-6 space-y-4 max-w-3xl">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">Tiêu đề</label>
              <input className="input" value={info.title} onChange={(e) => setI('title', e.target.value)} />
            </div>
            <div>
              <label className="label">Đường dẫn (slug)</label>
              <input className="input" value={info.slug} onChange={(e) => setI('slug', e.target.value)} />
            </div>
            <div>
              <label className="label">Vị trí mục tiêu</label>
              <select className="select" value={info.position || ''} onChange={(e) => setI('position', e.target.value)}>
                <option value="">— Chọn vị trí —</option>
                {positions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                {info.position && !positions.some((o) => o.value === info.position) && (
                  <option value={info.position}>{info.position} (giá trị cũ)</option>
                )}
              </select>
              <p className="text-2xs text-secondary-400 mt-1">
                Đồng bộ với <a href="/admin/options" target="_blank" rel="noreferrer" className="text-primary-600 underline">Cấu hình form → Vị trí</a>.
                Muốn thêm vị trí mới, thêm ở đó trước.
              </p>
            </div>
            <div>
              <label className="label">Trạng thái</label>
              <select className="select" value={info.is_active ? '1' : '0'} onChange={(e) => setI('is_active', e.target.value === '1')}>
                <option value="1">Đang mở tuyển</option>
                <option value="0">Đã đóng</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label">Mô tả ngắn (dưới tiêu đề)</label>
            <textarea className="input min-h-[70px]" value={info.description} onChange={(e) => setI('description', e.target.value)} />
          </div>
          <div>
            <label className="label">Nội dung giới thiệu</label>
            <textarea className="input min-h-[110px]" value={info.intro_content} onChange={(e) => setI('intro_content', e.target.value)} placeholder="Mô tả công việc, quyền lợi, yêu cầu..." />
          </div>
          <div>
            <label className="label">Ảnh đầu trang (hero)</label>
            <div className="flex items-center gap-3">
              <input className="input flex-1" value={info.hero_image} onChange={(e) => setI('hero_image', e.target.value)} placeholder="URL ảnh hoặc tải lên →" />
              <label className="btn-secondary cursor-pointer whitespace-nowrap">
                <HiOutlinePhotograph className="w-4 h-4" /> {uploadingHero ? 'Đang tải...' : 'Tải ảnh'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadHero(e.target.files?.[0])} />
              </label>
            </div>
            {info.hero_image && <img src={info.hero_image} alt="" className="mt-2 h-24 rounded-lg object-cover" />}
          </div>
          <div>
            <label className="label">Lời cảm ơn sau khi nộp</label>
            <textarea className="input min-h-[70px]" value={info.thank_you} onChange={(e) => setI('thank_you', e.target.value)} />
          </div>
          <button onClick={saveInfo} disabled={savingInfo} className="btn-primary">
            <HiOutlineSave className="w-5 h-5" /> {savingInfo ? 'Đang lưu...' : 'Lưu thông tin'}
          </button>
        </div>
      )}

      {/* TAB: TRƯỜNG DỮ LIỆU */}
      {tab === 'fields' && (
        <div className="max-w-3xl">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <select className="select w-auto" value="" onChange={(e) => {
              const sf = availableSystem.find((x) => x.field_key === e.target.value); if (sf) addSystem(sf)
            }}>
              <option value="">+ Thêm trường hệ thống…</option>
              {availableSystem.map((sf) => <option key={sf.field_key} value={sf.field_key}>{sf.label}</option>)}
            </select>
            <button onClick={addCustom} className="btn-secondary"><HiOutlinePlus className="w-4 h-4" /> Trường tùy biến</button>
            <div className="flex-1" />
            <button onClick={saveFields} disabled={savingFields} className="btn-primary">
              <HiOutlineSave className="w-5 h-5" /> {savingFields ? 'Đang lưu...' : 'Lưu các trường'}
            </button>
          </div>

          <div className="space-y-3">
            {fields.map((f, i) => {
              const locked = f.field_key === 'phone'
              const showOptions = !f.is_system && (f.field_type === 'select' || f.field_type === 'multiselect')
              return (
                <div key={i} className="card p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col pt-1">
                      <button onClick={() => move(i, -1)} className="text-secondary-300 hover:text-secondary-600"><HiOutlineChevronUp className="w-4 h-4" /></button>
                      <button onClick={() => move(i, 1)} className="text-secondary-300 hover:text-secondary-600"><HiOutlineChevronDown className="w-4 h-4" /></button>
                    </div>
                    <div className="flex-1 grid md:grid-cols-2 gap-3">
                      <div>
                        <label className="label flex items-center gap-1">Nhãn hiển thị {locked && <HiOutlineLockClosed className="w-3 h-3 text-secondary-300" />}</label>
                        <input className="input" value={f.label} onChange={(e) => setField(i, { label: e.target.value })}
                          placeholder="VD: Mức lương mong muốn" />
                      </div>
                      <div>
                        <label className="label">Loại trường</label>
                        {f.is_system ? (
                          <input className="input bg-secondary-50" value={`${FIELD_TYPE_LABELS[f.field_type]} (hệ thống)`} disabled />
                        ) : (
                          <select className="select" value={f.field_type} onChange={(e) => setField(i, { field_type: e.target.value })}>
                            {CUSTOM_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        )}
                      </div>
                      {!f.is_system && (
                        <div>
                          <label className="label">Khóa lưu (key)</label>
                          <input className="input font-mono text-xs" value={f.field_key}
                            onChange={(e) => setField(i, { field_key: slugifyKey(e.target.value) })}
                            placeholder="tự sinh từ nhãn" />
                        </div>
                      )}
                      {!['file', 'image', 'boolean', 'select', 'multiselect'].includes(f.field_type) && (
                        <div>
                          <label className="label">Gợi ý (placeholder)</label>
                          <input className="input" value={f.placeholder || ''} onChange={(e) => setField(i, { placeholder: e.target.value })} />
                        </div>
                      )}
                      {showOptions && (
                        <div className="md:col-span-2">
                          <label className="label">Các lựa chọn (mỗi dòng 1 mục)</label>
                          <textarea className="input min-h-[70px] text-sm" value={(f.options || []).join('\n')}
                            onChange={(e) => setField(i, { options: e.target.value.split('\n').map((x) => x.trim()).filter(Boolean) })} />
                        </div>
                      )}
                      {f.is_system && (f.field_type === 'select' || f.field_type === 'multiselect') && (
                        <div className="md:col-span-2">
                          <p className="text-2xs text-secondary-600 bg-primary-50 border border-primary-100 rounded px-2 py-1.5">
                            🔗 Lựa chọn lấy tự động từ <a href="/admin/options" target="_blank" rel="noreferrer" className="text-primary-600 underline font-medium">Cấu hình form</a> (đồng bộ — sửa 1 nơi mọi form cập nhật).
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 pt-1">
                      <button onClick={() => removeField(i)} disabled={locked}
                        className="p-1.5 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-30 disabled:cursor-not-allowed" title={locked ? 'SĐT không thể xóa' : 'Xóa trường'}>
                        <HiOutlineTrash className="w-4 h-4" />
                      </button>
                      <label className="flex items-center gap-1.5 text-2xs text-secondary-500 cursor-pointer whitespace-nowrap">
                        <input type="checkbox" checked={!!f.is_required} disabled={locked}
                          onChange={(e) => setField(i, { is_required: e.target.checked })} /> Bắt buộc
                      </label>
                      <label className="flex items-center gap-1.5 text-2xs text-secondary-500 cursor-pointer whitespace-nowrap">
                        <input type="checkbox" checked={!!f.is_active}
                          onChange={(e) => setField(i, { is_active: e.target.checked })} /> Hiển thị
                      </label>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          <p className="text-2xs text-secondary-400 mt-3">* Trường <b>Số điện thoại</b> luôn bắt buộc và không thể xóa (tối thiểu để liên hệ ứng viên).</p>
        </div>
      )}

      {/* TAB: XEM TRƯỚC */}
      {tab === 'preview' && (
        <div className="max-w-4xl">
          <div className="card p-4 mb-4 flex flex-wrap items-center gap-2">
            <code className="text-sm text-primary-600 bg-primary-50 px-3 py-1.5 rounded flex-1 truncate">{publicUrl(info.slug)}</code>
            <button onClick={() => { navigator.clipboard?.writeText(publicUrl(info.slug)); toast.success('Đã copy link') }} className="btn-secondary btn-sm">
              <HiOutlineClipboardCopy className="w-4 h-4" /> Copy
            </button>
            <a href={publicUrl(info.slug)} target="_blank" rel="noreferrer" className="btn-primary btn-sm">
              <HiOutlineExternalLink className="w-4 h-4" /> Mở form
            </a>
          </div>
          <div className="card overflow-hidden" style={{ height: 600 }}>
            <iframe title="preview" src={publicUrl(info.slug)} className="w-full h-full border-0" />
          </div>
          <p className="text-2xs text-secondary-400 mt-2">* Lưu thay đổi trước khi xem để preview phản ánh đúng.</p>
        </div>
      )}
    </div>
  )
}
